import _map from 'lodash/map';
import _reduce from 'lodash/reduce';
import gql from 'graphql-tag';

import { getDispensaryGaV4Client } from 'utils/analytics/clients/dispensary-ga-v4-client';
import { pushGTMDataLayer } from 'hooks/use-google-tag-manager';
import { cartItemToGaV4CartItem, productToGaV4CartItem } from 'utils/analytics/helpers/ga-v4-converters';
import { DEFAULT_CURRENCY } from 'utils/analytics/clients/ga-v4-client';
import {
  cartItemToGTMCartItem,
  ecommerceGTMMetaData,
  getDispensaryContext,
} from 'utils/analytics/trackers/internal-gtm-tracker/helpers/internal-gtm-converters';
import { GTM_V2_FLAG } from 'utils/analytics/constants/feature-flags';
import { recursiveKeyWalker } from 'shared/helpers/utils';

/**
 * @fileoverview
 *
 * This file is only used for the dispensary tracker setup as an integration, not by any of our own internal trackers
 * It's started being migrated over to dispensary-gtm-tracker.ts in the analytics framework
 * That file still calls this one since it's roots go deep. 150 points for house gryffindor if you delete this file safely.
 *
 * @deprecated - transitioning to new analytics framework (see {@link ./analytics/README.md})
 *
 */

const createAnalyticsPayload = gql`
  mutation SendAnalyticsPayload($payload: JSONObject!) {
    logAnalyticsPayload(payload: $payload) {
      success
    }
  }
`;

async function dispatchDebugEventTracker(gtmPayload = {}) {
  if (!window?._____APP_STATE_____) {
    return;
  }
  const { apolloClient, Cart } = window._____APP_STATE_____;

  if (!apolloClient?.mutate || !Cart) {
    return;
  }

  let payload = {
    dispensaryId: Cart.dispensary?.id,
    checkoutToken: Cart.checkoutToken,
    gtmPayload,
  };

  payload = recursiveKeyWalker(payload, (key) => key.replace(/\./g, '[dot]'));

  if (!payload.gtmPayload.event) {
    return;
  } // Don't send ID events

  await apolloClient.mutate({
    mutation: createAnalyticsPayload,
    variables: { payload },
  });
}

// let currentAnalyticsContext: AnalyticsContext | null = null;
let currentAnalyticsContext = null;

// export function setAnalyticsContext(context: AnalyticsContext): void {
/** @import { AnalyticsContext } from './analytics/event' */
/**
 * @param {AnalyticsContext} context
 */
export function setAnalyticsContext(context) {
  currentAnalyticsContext = context;
}

/**
 * Push an ecommerce payload to the datalayer if the GA v4 client isn't
 * operational (and therefore piggypacking the payload on GA v4 events).
 *
 * Without this check, we would transmit duplicate GTM ecommerce events.
 *
 * See ga-v4-client.ts -> attachLegacyEcommercePayload() for more details.
 */
export function maybePushEcommerceToDatalayer(payload) {
  // extend payload to contain the dispensary context
  // that just fills in id and stuff when that's missing, and keeps it synced with the internal trackers
  // we only do these extra extensions if we have an analytics context, this is moreso for unit test cases
  if (currentAnalyticsContext) {
    payload = {
      ...getDispensaryContext(currentAnalyticsContext, currentAnalyticsContext),
      ...payload,
    };

    if (payload.ecommerce) {
      // extend ecommerce block
      payload.ecommerce = {
        ...ecommerceGTMMetaData({ context: currentAnalyticsContext, contextCache: currentAnalyticsContext }),
        ...payload.ecommerce,
      };
    }
  }

  const gaV4ClientIsResonsibleForEcommerceEvents = getDispensaryGaV4Client().isCapturingEcommercePayload();
  if (!gaV4ClientIsResonsibleForEcommerceEvents) {
    pushGTMDataLayer(payload);
  }

  dispatchDebugEventTracker(payload);
}

export function productImpression(rawProduct, _cart) {
  const useV2 = !!currentAnalyticsContext?.featureFlags?.[GTM_V2_FLAG];

  if (useV2) {
    const itemPrice = rawProduct.Prices?.[0] != null ? parseFloat(String(rawProduct.Prices[0])) : 0;
    getDispensaryGaV4Client().viewItem({
      items: [productToGaV4CartItem(rawProduct)],
      currency: DEFAULT_CURRENCY,
      value: itemPrice,
    });
  } else {
    getDispensaryGaV4Client().viewItem({
      items: [productToGaV4CartItem(rawProduct)],
    });
    maybePushEcommerceToDatalayer({
      event: 'view_item',
      ecommerce: {
        items: [{ item_id: rawProduct.id, item_name: rawProduct.name, item_category: rawProduct.type }],
      },
    });
  }
}

export function productAddedToCart(cartItem, cart) {
  const useV2 = !!currentAnalyticsContext?.featureFlags?.[GTM_V2_FLAG];

  if (useV2) {
    const ga4Item = cartItemToGaV4CartItem(cartItem);
    const itemPrice = Number(ga4Item.price) || 0;
    const itemValue = parseFloat((itemPrice * (Number(ga4Item.quantity) || 1)).toFixed(2));
    getDispensaryGaV4Client().addToCart({
      items: [ga4Item],
      currency: DEFAULT_CURRENCY,
      value: itemValue,
    });
  } else {
    getDispensaryGaV4Client().addToCart({
      items: [cartItemToGaV4CartItem(cartItem)],
      currency: DEFAULT_CURRENCY,
      value: cartItem.unitPrice,
    });
    maybePushEcommerceToDatalayer({
      event: 'add_to_cart',
      ecommerce: {
        currency: DEFAULT_CURRENCY,
        unitPrice: cartItem.unitPrice,
        value: cart.costBreakdown?.total,
        affiliation: cart.dispensary?.name,
        items: [cartItemToGTMCartItem(cartItem, false)],
      },
    });
  }
}

export function productRemovedFromCart(cartItem, _cart) {
  const useV2 = !!currentAnalyticsContext?.featureFlags?.[GTM_V2_FLAG];

  if (useV2) {
    const ga4Item = cartItemToGaV4CartItem(cartItem);
    const itemPrice = Number(ga4Item.price) || 0;
    const itemValue = parseFloat((itemPrice * (Number(ga4Item.quantity) || 1)).toFixed(2));
    getDispensaryGaV4Client().removeFromCart({
      items: [ga4Item],
      currency: DEFAULT_CURRENCY,
      value: itemValue,
    });
  } else {
    getDispensaryGaV4Client().removeFromCart({
      items: [cartItemToGaV4CartItem(cartItem)],
      currency: DEFAULT_CURRENCY,
      value: cartItem.unitPrice,
    });
    maybePushEcommerceToDatalayer({
      event: 'remove_from_cart',
      ecommerce: {
        items: [cartItemToGTMCartItem(cartItem, false)],
      },
    });
  }
}

export function cartDidViewCheckout(cart) {
  const useV2 = !!currentAnalyticsContext?.featureFlags?.[GTM_V2_FLAG];
  const totalValue = _reduce(cart.items, (memo, item) => memo + (item.unitPrice || 0), 0);

  if (useV2) {
    const ga4Items = _map(cart.items, (item) => cartItemToGaV4CartItem(item));
    const numericValue = _reduce(ga4Items, (memo, item) => memo + (Number(item.price) || 0) * (item.quantity || 1), 0);
    getDispensaryGaV4Client().beginCheckout({
      items: ga4Items,
      currency: DEFAULT_CURRENCY,
      value: parseFloat(numericValue.toFixed(2)),
    });
  } else {
    getDispensaryGaV4Client().beginCheckout({
      items: _map(cart.items, (item) => cartItemToGaV4CartItem(item)),
      currency: DEFAULT_CURRENCY,
      value: totalValue,
    });
    maybePushEcommerceToDatalayer({
      event: 'begin_checkout',
      ecommerce: {
        items: _map(cart.items, (item) => cartItemToGTMCartItem(item, false)),
      },
    });
  }
}

export function cartDidPurchase(cart, serverOrder) {
  const useV2 = !!currentAnalyticsContext?.featureFlags?.[GTM_V2_FLAG];
  const purchaseValue = serverOrder.totalCost != null ? parseFloat(Number(serverOrder.totalCost).toFixed(2)) : 0;

  if (useV2) {
    getDispensaryGaV4Client().purchase({
      transactionId: serverOrder.orderId || serverOrder.id,
      affiliation: 'dutchie',
      value: purchaseValue,
      items: _map(cart.items, (item) => cartItemToGaV4CartItem(item)),
      currency: DEFAULT_CURRENCY,
    });
  } else {
    getDispensaryGaV4Client().purchase({
      transactionId: serverOrder.orderId || serverOrder.id,
      affiliation: 'dutchie',
      value: serverOrder.totalCost,
      items: _map(cart.items, (item) => cartItemToGaV4CartItem(item)),
      currency: DEFAULT_CURRENCY,
    });
    maybePushEcommerceToDatalayer({
      event: 'purchase',
      ecommerce: {
        transaction_id: serverOrder.orderId || serverOrder.id,
        affiliation: 'dutchie',
        value: serverOrder.totalCost,
        items: _map(cart.items, (item) => cartItemToGTMCartItem(item, false)),
      },
    });
  }
}

/** GA4 view_cart call. Only fires when v2 flag is on (GA4 client had no viewCart before). */
export function cartDidViewCart(cartItems, useV2) {
  if (!useV2) return;

  const ga4Items = _map(cartItems, (item) => cartItemToGaV4CartItem(item));
  const totalValue = _reduce(ga4Items, (memo, item) => memo + (Number(item.price) || 0) * (item.quantity || 1), 0);

  getDispensaryGaV4Client().viewCart({
    items: ga4Items,
    currency: DEFAULT_CURRENCY,
    value: parseFloat(totalValue.toFixed(2)),
  });
}

/** GA4 search call. Only fires when v2 flag is on. */
export function searchPerformed(query) {
  const useV2 = !!currentAnalyticsContext?.featureFlags?.[GTM_V2_FLAG];
  if (!useV2) return;

  getDispensaryGaV4Client().search({ searchTerm: query });
}

/** GA4 view_search_results call with product items. Only fires when v2 flag is on. */
export function searchResultsViewed(query, products) {
  const useV2 = !!currentAnalyticsContext?.featureFlags?.[GTM_V2_FLAG];
  if (!useV2) return;

  const ga4Items = _map(products, (p) => productToGaV4CartItem(p));
  getDispensaryGaV4Client().viewSearchResults({ searchTerm: query, items: ga4Items });
}
